import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Users, Network, Trophy, Shield, BookOpen, Brain } from 'lucide-react';
import SectionHeading from './SectionHeading';

const Leadership: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const leadershipRoles = [
    {
      title: 'Co-Founder',
      organization: 'DePauw AI/ML Club',
      imageName: 'DDSC.png',
      period: 'Aug 2025 – Present',
      location: 'Greencastle, IN',
      icon: Brain,
      description: 'Founded the AI/ML Club at DePauw University to foster student learning in artificial intelligence and machine learning. Initiated workshops and collaborative projects to bridge academic concepts with real-world AI/ML applications.',
      achievements: [
        'Organized weekly workshops on machine learning fundamentals',
        'Led collaborative projects connecting 20+ students with industry mentors',
        'Established partnerships with local tech companies for real-world projects'
      ]
    },
    {
      title: 'Teaching Assistant',
      organization: 'DePauw University',
      imageName: 'Depauw.png',
      period: 'Aug 2025 – Present',
      location: 'Greencastle, IN',
      icon: BookOpen,
      description: 'Assisted students with debugging, coding challenges, and understanding complex course concepts during lab sessions.',
      achievements: [
        'Mentored 30+ students in computer science fundamentals',
        'Conducted weekly office hours and review sessions',
        'Developed supplementary learning materials for challenging topics'
      ]
    },
    {
      title: 'IT Head',
      organization: 'DePauw Data Science Club',
      imageName: 'DDSC.png',
      period: 'Aug 2023 – Jan 2024',
      location: 'Greencastle, IN',
      icon: Network,
      description: 'Oversaw critical IT operations with 99% uptime, ensuring seamless connectivity and performance for all club activities. Built an interactive, informative platform, increasing user engagement by 50% and driving a 40% boost in site traffic.',
      achievements: [
        'Maintained 99% system uptime for all club events and activities',
        'Increased website traffic by 40% through improved user experience',
        'Implemented automated monitoring systems for critical infrastructure'
      ]
    },
    {
      title: 'Co-Founder',
      organization: 'Developer\'s Club',
      imageName: 'DLC.png',
      period: 'Jan 2023 – Aug 2024',
      location: 'Greencastle, IN',
      icon: Users,
      description: 'Launched and led a tech student org fostering innovation, collaboration, and peer-to-peer knowledge sharing.',
      achievements: [
        'Built a community of 50+ student developers',
        'Organized hackathons and coding competitions',
        'Facilitated peer-to-peer learning through code reviews and pair programming'
      ]
    },
    {
      title: 'Treasurer',
      organization: 'DePauw Cricket Club',
      imageName: 'CricketClub.jpg',
      period: 'Aug 2022 – May 2023',
      location: 'Greencastle, IN',
      icon: Trophy,
      description: 'Managed club finances, secured funding for new equipment, and organized budget-friendly inter-college matches.',
      achievements: [
        'Secured $5,000 in funding for new cricket equipment',
        'Organized 10+ inter-college tournaments within budget',
        'Improved financial transparency with monthly reports'
      ]
    },
    {
      title: 'Risk Head',
      organization: 'Sigma Chi Fraternity (Xi Chapter)',
      imageName: 'Sigchi.png',
      period: 'May 2024 – Dec 2024',
      location: 'Greencastle, IN',
      icon: Shield,
      description: 'Oversaw safety protocols and risk mitigation strategies for all chapter events, ensuring full compliance.',
      achievements: [
        'Implemented comprehensive safety protocols for all events',
        'Achieved 100% compliance with university and national guidelines',
        'Trained 40+ members on safety and risk management procedures'
      ]
    }
  ];

  return (
    <section id="leadership" className="relative py-24">
      <div className="section-shell">
        <div ref={ref}>
          <SectionHeading
            eyebrow="Leadership"
            title={<>Communities, mentorship, and <span className="gradient-text-vibrant">high-ownership roles.</span></>}
            description="I enjoy building more than products. I also build teams, systems, and momentum for the communities around me."
          />
        </div>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {leadershipRoles.map((role, index) => (
            <motion.div
              key={`${role.organization}-${role.title}`}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="group"
            >
              <motion.div
                whileHover={{ 
                  scale: 1.02, 
                  y: -4,
                  boxShadow: "0 22px 50px rgba(96, 165, 250, 0.12)"
                }}
                transition={{ duration: 0.4 }}
                className="surface-panel surface-glow spotlight-card h-full rounded-[28px] border border-white/10 transition-all duration-300 hover:border-cosmic-aurora-emerald/50"
              >
                <div className="mb-4 flex items-start space-x-4">
                  <img 
                    src={`/Images/${role.imageName}`} 
                    alt={`${role.organization} Logo`}
                    className="h-12 w-12 flex-shrink-0 rounded-2xl bg-slate-100 object-contain p-2 transition-transform duration-300 group-hover:scale-110 dark:bg-white/5"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-slate-900 transition-colors duration-300 group-hover:text-cosmic-aurora-emerald dark:text-white">
                      {role.title}
                    </h3>
                    <h4 className="text-md font-semibold text-cosmic-aurora-emerald">
                      {role.organization}
                    </h4>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                      <span>{role.period}</span>
                      <span>•</span>
                      <span>{role.location}</span>
                    </div>
                  </div>
                </div>

                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs uppercase tracking-[0.2em] text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
                  <role.icon className="h-3.5 w-3.5" />
                  Leadership role
                </div>

                <p className="mb-4 text-sm leading-7 text-slate-700 dark:text-slate-300">
                  {role.description}
                </p>

                <div className="space-y-2">
                  <h5 className="text-sm font-semibold text-slate-900 dark:text-white">Key achievements</h5>
                  <ul className="space-y-2">
                    {role.achievements.map((achievement, idx) => (
                      <li key={idx} className="flex items-start space-x-2 rounded-2xl bg-slate-100 px-3 py-2 text-xs leading-6 text-slate-700 dark:bg-white/[0.03] dark:text-slate-300">
                        <span className="mt-1 text-cosmic-aurora-emerald">•</span>
                        <span>{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Leadership;
